/**
 * Text Designer utils: validations, constants, font-family helpers.
 */

/// <reference path="./css-font-parser.d.ts" />
import { parseFontFamily } from 'css-font-parser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TypographyVariant = {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: number;
  lineHeight?: string;
  letterSpacing?: string;
  color?: string;
};

/** Keys of TypographyVariant that have validation (excludes fontFamily). */
export type FieldErrorKey = Exclude<keyof TypographyVariant, 'fontFamily'>;

export type TypographyTokens = {
  [key: string]: TypographyVariant;
};

export type OrganizationState = {
  id?: string;
  themeTokens?: {
    comp?: {
      typography?: TypographyTokens;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  useParentThemeTokens?: boolean;
};

// ---------------------------------------------------------------------------
// Font family parse / format
// ---------------------------------------------------------------------------

/** Parse a CSS font-family string into an array of font names (uses css-font-parser; handles quoted, unquoted, and escaped). */
export function parseFontFamilyString(cssValue: string): string[] {
  if (!cssValue?.trim()) return [];
  const trimmed = cssValue.replace(/\s*;\s*$/, '').trim();
  const parsed = parseFontFamily(trimmed);
  return parsed ?? [];
}

/** Format an array of font names into a CSS font-family value (quote names with spaces). */
export function formatFontFamilyString(fontNames: string[]): string {
  const parts = fontNames.filter(Boolean).map((name) => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    if (trimmed.includes(' ') || trimmed.includes(',')) return `"${trimmed}"`;
    return trimmed;
  });
  return parts.filter(Boolean).join(', ');
}

export function isFontOptionEqual(option: string, val: string): boolean {
  return String(option).trim() === String(val).trim();
}

// ---------------------------------------------------------------------------
// Validations
// ---------------------------------------------------------------------------

export function validateFontSize(
  value: string | undefined,
): FieldErrorKey | null {
  const s = String(value ?? '').trim();
  if (!s) return null;
  if (/^\d+(\.\d+)?(px|em|rem)$/.test(s)) return null;
  return 'fontSize';
}

export function validateFontWeight(
  value: string | number | undefined,
): FieldErrorKey | null {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isNaN(n) || !Number.isInteger(n) || n < 1 || n > 1000)
    return 'fontWeight';
  return null;
}

export function validateLetterSpacing(
  value: string | undefined,
): FieldErrorKey | null {
  const s = String(value ?? '').trim();
  if (!s) return null;
  if (s.toLowerCase() === 'normal') return null;
  if (/^-?\d+(\.\d+)?(px|em|rem)?$/.test(s)) return null;
  return 'letterSpacing';
}

export function validateLineHeight(
  value: string | undefined,
): FieldErrorKey | null {
  const s = String(value ?? '').trim();
  if (!s) return null;
  if (s.toLowerCase() === 'normal') return null;
  if (/^\d+(\.\d+)?(px|em|rem)?$/.test(s) || /^\d+(\.\d+)?$/.test(s))
    return null;
  return 'lineHeight';
}

export function validateColor(value: string | undefined): FieldErrorKey | null {
  const s = String(value ?? '').trim();
  if (!s) return null;
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(s)) return null;
  return 'color';
}

/** Run the validator for a given field; returns the error key if invalid. */
export function validateField(
  field: FieldErrorKey,
  value: string | number | undefined,
): FieldErrorKey | null {
  switch (field) {
    case 'fontSize':
      return validateFontSize(value as string);
    case 'fontWeight':
      return validateFontWeight(value);
    case 'letterSpacing':
      return validateLetterSpacing(value as string);
    case 'lineHeight':
      return validateLineHeight(value as string);
    case 'color':
      return validateColor(value as string);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// i18n keys for validation messages
// ---------------------------------------------------------------------------

export const VALIDATION_MESSAGE_KEYS: Record<FieldErrorKey, string> = {
  fontSize: 'validationFontSize',
  fontWeight: 'validationFontWeight',
  letterSpacing: 'validationLetterSpacing',
  lineHeight: 'validationLineHeight',
  color: 'validationColor',
};

// ---------------------------------------------------------------------------
// Constants: typography variants, font list, example text
// ---------------------------------------------------------------------------

export const TYPOGRAPHY_VARIANTS = [
  'displayLargeVariant',
  'displayMediumVariant',
  'displayLarge',
  'displayMedium',
  'displaySmall',
  'headingLarge',
  'headingMedium',
  'headingSmall',
  'headingXSmall',
  'headingOverline',
  'titleLarge',
  'titleMedium',
  'bodyLarge',
  'bodyMedium',
  'bodySmall',
  'labelLarge',
  'labelSmall',
  'button',
  'trimmedXSmall',
  'trimmedSmall',
  'trimmedNormal',
  'trimmedMedium',
  'trimmedLarge',
  'monospacedXlarge',
  'monospacedLarge',
  'monospacedSmall',
] as const;

export const COMMON_FONT_FAMILIES = [
  'Arial',
  'ATB TT Norms',
  'Georgia',
  'Helvetica',
  'Helvetica Neue',
  'Inter',
  'Manier',
  'Roboto',
  'Segoe UI',
  'sans-serif',
  'serif',
  'Source Sans',
  'Tiempos Headline',
  'Tiempos Text',
  'Times New Roman',
];

const DEFAULT_EXAMPLE = {
  title: 'What is Lorem Ipsum?',
  body: `Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged.`,
};

export const EXAMPLE_TEXT: Record<string, { title: string; body: string }> = {
  displayLargeVariant: {
    title: 'Display Large Variant',
    body: 'This is the largest display text style for hero sections and major headlines.',
  },
  displayMediumVariant: {
    title: 'Display Medium Variant',
    body: 'A medium-sized display text for secondary headlines.',
  },
  displayLarge: {
    title: 'Display Large',
    body: 'Large display typography for prominent headings.',
  },
  displayMedium: {
    title: 'Display Medium',
    body: 'Medium display text for section titles.',
  },
  displaySmall: {
    title: 'Display Small',
    body: 'Smaller display text for sub-sections.',
  },
  headingLarge: {
    title: 'Heading Large',
    body: 'Large headings for major content sections.',
  },
  headingMedium: {
    title: 'Heading Medium',
    body: 'Medium headings for content organization.',
  },
  headingSmall: {
    title: 'Heading Small',
    body: 'Small headings for minor sections.',
  },
  headingXSmall: {
    title: 'Heading Extra Small',
    body: 'Extra small headings for detailed content.',
  },
  headingOverline: {
    title: 'OVERLINE TEXT',
    body: 'Used for labels above headings.',
  },
  titleLarge: {
    title: 'Title Large',
    body: 'Large titles for cards and components.',
  },
  titleMedium: {
    title: 'Title Medium',
    body: 'Medium titles for UI elements.',
  },
  bodyLarge: {
    title: 'Body Large Text',
    body: 'This is large body text used for important paragraphs and content that needs emphasis. It provides better readability for longer content.',
  },
  bodyMedium: {
    title: 'Body Medium Text',
    body: 'This is medium body text, the standard size for most content. It balances readability with information density.',
  },
  bodySmall: {
    title: 'Body Small Text',
    body: 'This is small body text for secondary information, footnotes, and supporting content.',
  },
  labelLarge: {
    title: 'Label Large',
    body: 'Used for form labels and UI element labels.',
  },
  labelSmall: {
    title: 'Label Small',
    body: 'Smaller labels for compact UI areas.',
  },
  button: { title: 'Button Text', body: 'SUBMIT    CANCEL    SAVE CHANGES' },
  trimmedXSmall: { title: 'Trimmed XS', body: '$1,234.56' },
  trimmedSmall: { title: 'Trimmed Small', body: '$12,345.67' },
  trimmedNormal: { title: 'Trimmed Normal', body: '$123,456.78' },
  trimmedMedium: { title: 'Trimmed Medium', body: '$1,234,567.89' },
  trimmedLarge: { title: 'Trimmed Large', body: '$12,345,678.90' },
  monospacedXlarge: { title: 'Monospaced XL', body: '0123456789' },
  monospacedLarge: { title: 'Monospaced Large', body: '0123456789' },
  monospacedSmall: { title: 'Monospaced Small', body: '0123456789' },
};

/** Return example text for a variant, or default. */
export function getExampleTextForVariant(variant: string): {
  title: string;
  body: string;
} {
  return EXAMPLE_TEXT[variant] ?? DEFAULT_EXAMPLE;
}
